/**
 * @module InsertableReplyHistoryPlugin
 *
 * An insertable Email Reply feature for ProseMirror.
 *
 * ## Overview
 *
 * This plugin displays a small button at the bottom of the editor. When clicked,
 * it will copy the reply history of the message and insert it in place of the
 * button. This can be undone with the undo button.
 *
 * This plugin is currently only used for Email replies and forwards.
 *
 * To use this plugin, you must provide it with a fetcher function that will
 * resolve to a string of HTML containing the reply history. The plugin will
 * convert this HTML into ProseMirror content and insert it into the editor.
 *
 * A typical plugin configuration will look like this:
 *
 * ```ts
 * createInsertableReplyHistoryPlugin({
 *   autoInsert: true,
 *   replyFetcher: () => fetchReplyHistory('message-id-here', 'REPLY'),
 *   signatureFetcher: () => fetchSignature('message-id-here'), // Optional, if you want signatures
 * });
 * ```
 *
 * Typically, your fetchers are API requests to load the reply data. They take no
 * arguments and return a promise that resolves to a string of HTML.
 *
 * Providing the `autoInsert` option will automatically insert the reply history
 * button when the plugin is initialized.
 *
 * ## Fetcher Function Details
 *
 * There is currently no way to change the fetcher function or aguments after
 * the plugin is initialized, as the typical lifecycle in Email editing is to
 * initialize a new HubSpot RTE for each email.
 *
 * The provided fetcher function is not called until the button is clicked.
 * The button will be disabled while the fetch is in progress, and enabled
 * again if the fetch fails.
 *
 * The fetcher function can return `null` to indicate that there is no content
 * to insert. In this case, the button will be removed from the document.
 *
 * The contents of the reply history is not cached within the plugin. However,
 * almost any API call will be cached properly by the data fetching client.
 *
 * ## Plugin State
 *
 * The plugin will react to certain transactions that manipulate the reply contents
 * or button. When it detects that the replies were inserted by the button, it will
 * mark the state as `repliesAlreadyInserted`. This is relevant to publishing the
 * message to the Email API, as it needs to know if email replies are already present
 * on the message body. Using this plugin state, you know whether to ask the backend
 * to append previous replies when sending.
 *
 * One special case handled by this plugin is when the replies are inserted by the
 * button and then later deleted by the user. In this case, `repliesAlreadyInserted`
 * will remain `true`. Use this indicator to know that the user expressly deleted
 * the reply chain, and that the reply chain should not be appended when sending.
 *
 * ## Migrating from EmailReplyHistoryPlugin
 *
 * This plugin is a successor to the `EmailReplyHistoryPlugin`, and while it re-uses
 * the emailReplyHistory node type, it does not use the "hidden" attribute
 * because there is no toggling behavior.
 *
 * For users who have no saved draft data in their browser, there is no issue. They
 * will receive clean EditorStates when they load the editor with the new plugin.
 *
 * For users who have some saved draft data, there is a migration
 * case to handle. If there is a reply history node already appended to the
 * document, we need to detect whether that node was inserted by the new
 * plugin or the old plugin. If it was inserted by the old plugin, we need
 * to delete it and insert the injector button to match the new behavior.
 *
 * The giveaway is the "hidden" attribute. If the node a "hidden" attribute value
 * of "true", then it was definitely inserted by the old plugin. Again, we have
 * no toggle behavior in the new plugin, so we cannot have a hidden reply history.
 * InsertableReplyHistoryPlugin always injects a history node with a "hidden" attribute
 * of "false".
 *
 * This case is handled automatically by the plugin during initialization.
 *
 */

import I18n from 'I18n';
import { EllipsesIcon } from 'foundations-assets';
import { Fragment, Slice } from 'prosemirror-model';
import { Plugin, PluginKey, NodeSelection, TextSelection } from 'prosemirror-state';
import { getDocNodeFromHtml } from '../ProsemirrorUtils';
import { schema } from '../ProsemirrorSchema';
import { Tracker } from '../../utils/usageTracker';
import { PM_REPLY_HISTORY } from '../constants/events';
import { createRoot } from 'react-dom/client';
import { flushSync } from 'react-dom';
import Raven from 'raven-js';
import styled from 'styled-components';
import { jsx as _jsx } from "react/jsx-runtime";
export const insertableReplyHistoryPluginKey = new PluginKey('insertableReplyHistory');

/**
 * Uses React components from the `UIComponents` package to render the static
 * button HTML. This is not ideal, but rendering React components as a child
 * of a view like this is one of our most bug-prone features, so we avoid it.
 */
const MEMOIZED_BUTTON_HTML = (() => {
  const ButtonHtmlContainer = document.createElement('div');
  const ButtonHtmlRoot = createRoot(ButtonHtmlContainer);
  flushSync(() => {
    const ButtonWrapper = styled.div.withConfig({
      displayName: "InsertableReplyHistoryPlugin__ButtonWrapper",
      componentId: "sc-1qrai13-0"
    })(["border-radius:var(--trellis-border-radius-400);border-color:var(--trellis-color-fill-accent-blue-hover);border-width:var(--trellis-border-width-100);border-style:solid;background-color:var(--trellis-color-fill-surface-raised);cursor:pointer;display:inline-flex;align-items:center;justify-content:center;width:45px;height:21px;color:var(--trellis-color-text-core-default);&:hover{background-color:var(--trellis-color-fill-surface-hover);}"]);
    ButtonHtmlRoot.render(
    /*#__PURE__*/
    // @bend-expect trellis-tokenized
    _jsx(ButtonWrapper, {
      "data-test-id": "InsertableReplyHistoryToggleButton",
      children: /*#__PURE__*/_jsx(EllipsesIcon, {
        size: "600"
      })
    }));
  });
  return ButtonHtmlContainer.innerHTML;
})();

/**
 * Finds the reply history node in the editor state.
 *
 * @param state The editor state.
 * @returns The reply history node, or null if it is not found.
 */
function findReplyHistoryNodeInState(state) {
  const doc = state.doc;
  for (let childIndex = doc.childCount; childIndex >= 0; childIndex--) {
    const node = doc.maybeChild(childIndex);
    if ((node === null || node === void 0 ? void 0 : node.type.name) === 'emailReply' || (node === null || node === void 0 ? void 0 : node.type.name) === 'emailReplyHistory') {
      return node;
    }
  }
  return null;
}

/**
 * Check if the EditorState already has some email reply content inside it.
 * This is used to determine if we should insert a reply button or not.
 */
function editorHasReplies(state) {
  return Boolean(findReplyHistoryNodeInState(state));
}

/**
 * Check if the EditorState contains a reply history from the old plugin.
 * It's the same emailReplyHistory node, but with the "hidden" attribute
 * set to true.
 */
function editorHasCollapsedRepliesFromOldPlugin(state) {
  const doc = state.doc;
  for (let childIndex = doc.childCount; childIndex >= 0; childIndex--) {
    const node = doc.maybeChild(childIndex);
    if ((node === null || node === void 0 ? void 0 : node.type.name) === 'emailReplyHistory' && node.attrs.hidden) {
      return true;
    }
  }
  return false;
}

/**
 * Check if the EditorState already has a reply button inside it.
 */
function editorHasReplyButton(state) {
  const doc = state.doc;
  for (let childIndex = doc.childCount; childIndex >= 0; childIndex--) {
    const node = doc.maybeChild(childIndex);
    if ((node === null || node === void 0 ? void 0 : node.type.name) === 'replyInserterButton') {
      return true;
    }
  }
  return false;
}

/**
 * Check the current plugin state to see if replies have already been inserted.
 *
 * @param state The current editor state.
 * @returns Whether the replies were inserted by the button.
 */
function areRepliesAlreadyInserted(state) {
  var _insertableReplyHisto;
  return (_insertableReplyHisto = insertableReplyHistoryPluginKey.getState(state)) === null || _insertableReplyHisto === void 0 ? void 0 : _insertableReplyHisto.repliesAlreadyInserted;
}

/**
 * Returns true if the reply node has been modified.
 *
 * @param state The current editor state.
 * @returns Whether the reply node has been modified.
 */
function haveRepliesBeenModified(state) {
  const replyNode = findReplyHistoryNodeInState(state);
  if (!replyNode) {
    return false;
  }
  return !replyNode.attrs.pristine;
}
export const helpers = {
  editorHasReplies,
  editorHasReplyButton,
  areRepliesAlreadyInserted,
  editorHasCollapsedRepliesFromOldPlugin,
  haveRepliesBeenModified
};

/**
 * Inserts a reply button into the editor. Will not insert a duplicate component.
 * For performance, searches document children from the end, exploiting the fact
 * that the reply button is always at the top level of the document and usually
 * at the end.
 *
 * @param state The current editor state.
 * @param dispatch The dispatch function.
 * @returns Whether the command was successful.
 */
const insertReplyButton = (state, dispatch) => {
  const doc = state.doc;
  for (let childCount = doc.childCount; childCount >= 0; childCount--) {
    const node = doc.maybeChild(childCount);
    if ((node === null || node === void 0 ? void 0 : node.type.name) === 'replyInserterButton') {
      return false;
    }
  }
  dispatch === null || dispatch === void 0 || dispatch(state.tr.insert(state.doc.content.size, state.schema.nodes.replyInserterButton.create()).setMeta('addToHistory', false));
  return true;
};

/**
 * Deletes a reply button from the editor.
 */
const deleteReplyButton = (state, dispatch) => {
  const doc = state.doc;
  let found = false;
  doc.forEach((node, offset) => {
    if (!found && node.type.name === 'replyInserterButton') {
      const nodePos = doc.resolve(offset);
      const nodeSelection = NodeSelection.create(doc, nodePos.pos);
      const tr = state.tr;
      tr.setSelection(nodeSelection);
      tr.deleteSelection();
      dispatch === null || dispatch === void 0 || dispatch(tr);
      found = true;
    }
  });
  return found;
};

/**
 * Replaces the reply button with the reply history. Returns false if there is no
 * reply button to replace, or if no content exists to replace.
 *
 * @param state The current editor state.
 * @param dispatch The dispatch function.
 * @returns Whether the command was successful.
 */
const replaceButtonWithHistory = withContent => (state, dispatch) => {
  const tr = state.tr;
  // Delete the button
  let foundButton = false;
  tr.doc.forEach((node, offset) => {
    if (node.type.name === 'replyInserterButton') {
      const nodePos = tr.doc.resolve(offset);
      const nodeSelection = NodeSelection.create(tr.doc, nodePos.pos);
      tr.setSelection(nodeSelection);
      foundButton = true;
    }
  });
  if (!foundButton) {
    return false;
  }

  // Append a paragraph at the end of the document and put the reply history
  // content in it.
  const contentSlice = new Slice(withContent, 0, 0);
  tr.replaceSelection(contentSlice);
  dispatch === null || dispatch === void 0 || dispatch(tr);
  return true;
};

/**
 * Replaces an existing reply history node with the inserter button.
 * @param state
 * @param dispatch
 * @returns
 */
const replaceHistoryWithButton = (state, dispatch) => {
  const tr = state.tr;

  // Delete the replies
  let foundHistory = false;
  tr.doc.forEach((node, offset) => {
    if (node.type.name === 'emailReplyHistory') {
      const nodePos = tr.doc.resolve(offset);
      const nodeSelection = NodeSelection.create(tr.doc, nodePos.pos);
      tr.setSelection(nodeSelection);
      foundHistory = true;
    }
  });
  if (!foundHistory) {
    return false;
  }

  // Insert the button
  tr.replaceSelectionWith(state.schema.nodes.replyInserterButton.create());
  dispatch === null || dispatch === void 0 || dispatch(tr);
  return true;
};

/**
 * Inserts the reply history at the bottom of the document.
 * Used when the reply button is deleted or cannot be found.
 */
const insertHistoryAtBottom = content => (state, dispatch) => {
  const tr = state.tr;
  tr.insert(state.doc.content.size, content);
  dispatch === null || dispatch === void 0 || dispatch(tr);
  return true;
};

/**
 * Deletes the reply history node from the document.
 */
const deleteReplies = (state, dispatch) => {
  const tr = state.tr;

  // Select the reply history node
  let foundHistory = false;
  tr.doc.forEach((node, offset) => {
    if (node.type.name === 'emailReplyHistory') {
      const nodePos = tr.doc.resolve(offset);
      const nodeSelection = NodeSelection.create(tr.doc, nodePos.pos);
      tr.setSelection(nodeSelection);
      foundHistory = true;
    }
  });
  if (!foundHistory) {
    return false;
  }

  // Delete the reply history node
  tr.deleteSelection();
  dispatch === null || dispatch === void 0 || dispatch(tr);
  return true;
};

/**
 * A collection of commands for the InsertableReplyHistory plugin.
 */
export const commands = {
  insertReplyButton,
  deleteReplyButton,
  replaceButtonWithHistory,
  insertHistoryAtBottom,
  replaceHistoryWithButton,
  deleteReplies
};

/**
 * Creates the reply fragment from the given HTML, including the signature if provided.
 *
 * @param options The options for creating the reply fragment.
 * @param options.html The HTML string to convert.
 * @param options.signature The signature to include in the reply fragment.
 * @param options.spacer Whether to add a blank line between the existing content and the reply content.
 * @returns The ProseMirror Fragment.
 */
function replyFragmentFromHtml({
  html,
  signature,
  spacer
}) {
  const fragmentsToInsert = [];

  // If requested, add a blank line between the existing content and the reply content.
  if (spacer) {
    const blankLine = schema.nodes.paragraph.create({
      textAlign: '',
      direction: ''
    }, null);
    fragmentsToInsert.push(blankLine);
  }

  // Parse and create a document node from the HTML
  const docNode = getDocNodeFromHtml(html, {
    preserveNewlines: false
  }).content.cut(0).firstChild;
  fragmentsToInsert.push(docNode);

  // If the signature is provided, append it to the fragment. This matches
  // the HubSpot backend behavior, which puts signatures after the reply content.
  if (signature) {
    const signatureNode = schema.nodes.signature.create({
      signature
    });
    fragmentsToInsert.push(signatureNode);
  }
  return expandHistoryNodesInFragment(Fragment.fromArray(fragmentsToInsert));
}

/**
 * Updates the attributes of all 'emailReplyHistory' nodes in a fragment to
 * the following values:
 *
 * - hidden: false
 * - expanded: true
 *
 * This will descend into each child of the given Fragment, but stops at
 * the level it finds a matching node.
 *
 * Returns a new Fragment with matching nodes updated.
 *
 * @param fragment The fragment to expand.
 * @returns The expanded fragment.
 */
function expandHistoryNodesInFragment(fragment) {
  let workingFragment = Fragment.from(fragment);
  fragment.descendants((node, pos) => {
    if (node.type.name === 'emailReplyHistory') {
      workingFragment = workingFragment.replaceChild(fragment.findIndex(pos).index, node.type.create({
        hidden: false,
        expanded: true
      }, node.content, node.marks));
      return false;
    }
    return true;
  });
  return workingFragment;
}

/**
 * Returns true if the editor state has existing content.
 *
 * @param state The editor state.
 * @returns Whether the editor state has existing content.
 */
function stateHasExistingContent(state) {
  const doc = state.doc;
  return doc.textContent.trim().length > 0;
}

/**
 * Returns true if the two editor states have the same reply history node.
 * Two states with no reply history nodes are considered equal.
 *
 * @param state1 The first editor state.
 * @param state2 The second editor state.
 * @returns Whether the two editor states have the same reply history node.
 */
function areReplyNodesInStatesEqual(state1, state2) {
  const historyNode1 = findReplyHistoryNodeInState(state1);
  const historyNode2 = findReplyHistoryNodeInState(state2);
  if (historyNode1 && historyNode2) {
    return historyNode1.eq(historyNode2);
  }
  return !historyNode1 && !historyNode2;
}

/**
 * A NodeView for the reply inserter button.
 *
 * This is a NodeView that renders a button and handles clicks.
 * By encapsulating the click handler here, we avoid a transaction to
 * handle its enabled/disabled state.
 */
class ReplyButtonNodeView {
  constructor(view) {
    this.handleClick = () => {
      // Fetch content, then insert it
      const pluginState = insertableReplyHistoryPluginKey.getState(this.view.state);
      if (pluginState) {
        const replyFetcher = pluginState.replyFetcher;
        const signatureFetcher = pluginState.signatureFetcher;
        if (replyFetcher) {
          // Disable the button while we load
          this.disableButton();
          const fetchPromise = Promise.resolve(replyFetcher());
          const signaturePromise = signatureFetcher ? Promise.resolve(signatureFetcher()) : Promise.resolve(null);
          Promise.all([fetchPromise, signaturePromise]).then(([html, signature]) => {
            // The fetcher may return nothing, indicating there are no replies.
            // Remove the button in this case.
            if (html === null) {
              deleteReplyButton(this.view.state, this.view.dispatch);
              return;
            }
            const requireSpacer = stateHasExistingContent(this.view.state);
            const cursorBeforeReplacement = this.view.state.selection;
            const replyFragment = replyFragmentFromHtml({
              html,
              signature,
              spacer: requireSpacer
            });
            const couldReplace = replaceButtonWithHistory(replyFragment)(this.view.state, this.view.dispatch);
            if (!couldReplace) {
              // If the button was deleted, insert the content at the bottom
              // of the document.
              insertHistoryAtBottom(replyFragment)(this.view.state, this.view.dispatch);
            }

            // Restore the previous cursor position
            this.view.dispatch(this.view.state.tr.setSelection(TextSelection.between(this.view.state.doc.resolve(cursorBeforeReplacement.from), this.view.state.doc.resolve(cursorBeforeReplacement.to))).setMeta('addToHistory', false).setMeta('repliesAlreadyInserted', true));
            Tracker.track(PM_REPLY_HISTORY, {
              action: 'toggle-reply-history-visibility',
              value: 'show'
            });
          }).catch(() => {
            // In case of a fetch failure, treat the issue as transient.
            // Restore button state so it can be clicked again.
            this.enableButton();
          });
        }
      }
    };
    this.dom = document.createElement('div');
    this.view = view;
    // Sorry, but we have to do this. No React components allowed here.
    // eslint-disable-next-line no-restricted-syntax
    this.dom.innerHTML = MEMOIZED_BUTTON_HTML;
    try {
      // We can't access I18n during the initial button rendering, so we set the
      // title attribute here.
      const buttonTitleText = I18n.text('hubspot-prosemirror.replyHistory.insertButtonTitle');
      this.dom.children[0].setAttribute('title', buttonTitleText);
      this.dom.children[0].setAttribute('aria-label', buttonTitleText);
      this.dom.children[0].addEventListener('click', this.handleClick);
    } catch (error) {
      Raven.captureException(error, {
        extra: {
          info: 'Failed to add click listener to button in ReplyButtonNodeView'
        }
      });
    }
  }
  disableButton() {
    var _this$dom$children$, _this$dom$children$2;
    (_this$dom$children$ = this.dom.children[0]) === null || _this$dom$children$ === void 0 || _this$dom$children$.setAttribute('disabled', 'true');
    (_this$dom$children$2 = this.dom.children[0]) === null || _this$dom$children$2 === void 0 || _this$dom$children$2.setAttribute('aria-disabled', 'true');
  }
  enableButton() {
    var _this$dom$children$3, _this$dom$children$4;
    (_this$dom$children$3 = this.dom.children[0]) === null || _this$dom$children$3 === void 0 || _this$dom$children$3.removeAttribute('disabled');
    (_this$dom$children$4 = this.dom.children[0]) === null || _this$dom$children$4 === void 0 || _this$dom$children$4.removeAttribute('aria-disabled');
  }
  update() {
    return false;
  }
  destroy() {
    var _this$dom$children$5;
    (_this$dom$children$5 = this.dom.children[0]) === null || _this$dom$children$5 === void 0 || _this$dom$children$5.removeEventListener('click', this.handleClick);
  }
}
/**
 * Creates a ProseMirror plugin that adds a Gmail-style reply history button
 * to the editor.
 */
function createInsertableReplyHistoryPlugin({
  replyFetcher,
  signatureFetcher,
  autoInsert = true
}) {
  return new Plugin({
    key: insertableReplyHistoryPluginKey,
    state: {
      init() {
        return {
          replyFetcher,
          signatureFetcher,
          repliesAlreadyInserted: false
        };
      },
      apply(tr, previousState) {
        const repliesAlreadyInsertedMeta = tr.getMeta('repliesAlreadyInserted');
        if (typeof repliesAlreadyInsertedMeta === 'boolean') {
          return Object.assign({}, previousState, {
            repliesAlreadyInserted: repliesAlreadyInsertedMeta
          });
        }
        return previousState;
      }
    },
    appendTransaction(trs, oldState, newState) {
      // --- Handling Undo and Redo over the reply insertion ---

      // First case: Button existed before, and was deleted.
      if (editorHasReplyButton(oldState) && !editorHasReplyButton(newState)) {
        // ...and there were no replies before, but now there are.
        if (!editorHasReplies(oldState) && editorHasReplies(newState)) {
          // Mark that the replies were inserted by the button.
          return newState.tr.setMeta('repliesAlreadyInserted', true);
        }
      }

      // Second case: Button did not exist before, and was inserted.
      if (!editorHasReplyButton(oldState) && editorHasReplyButton(newState)) {
        // ...and there WERE replies before, but now there are NONE.
        if (editorHasReplies(oldState) && !editorHasReplies(newState)) {
          // Mark that the replies were reverted.
          return newState.tr.setMeta('repliesAlreadyInserted', false);
        }
      }

      // --- Watching for changes in the reply node ---

      // Do we have replies, and did the document change?
      if (editorHasReplies(oldState) && trs.some(tr => tr.docChanged)) {
        // Does the reply node have attribute pristine = true?
        const replyNode = findReplyHistoryNodeInState(oldState);
        if (replyNode !== null && replyNode !== void 0 && replyNode.attrs.pristine) {
          if (!areReplyNodesInStatesEqual(oldState, newState)) {
            // Find the new reply node the expensive way so we can resolve its position.
            let replyNodePos = -1;
            newState.doc.descendants((node, pos) => {
              if (node.type.name === 'emailReplyHistory') {
                replyNodePos = pos;
              }
              return false;
            });
            if (replyNodePos === -1) {
              // For some reason, the reply node position was not found. Do nothing.
              return null;
            }

            // Append a transaction to mark the node as modified.
            return newState.tr.setNodeMarkup(replyNodePos, undefined, Object.assign({}, replyNode.attrs, {
              pristine: false
            }));
          }
        }
      }
      return null;
    },
    props: {
      nodeViews: {
        replyInserterButton: (_, view) => new ReplyButtonNodeView(view)
      }
    },
    /**
     * Automatically insert the reply history button when the plugin is initialized.
     *
     * @param view The editor view.
     * @returns An empty PluginView.
     */
    view(view) {
      if (autoInsert) {
        // Wait for the editor to be fully initialized before inserting the button.
        // This is due to the fact that editor state is owned by a React component,
        // and dispatching a transaction from the plugin will attempt to update the
        // editor state during a render cycle.
        setTimeout(() => {
          if (!editorHasReplies(view.state)) {
            insertReplyButton(view.state, view.dispatch);
          } else if (editorHasCollapsedRepliesFromOldPlugin(view.state)) {
            // If the replies were collapsed from the old plugin, delete them
            // before inserting the new button. This handles the migration case
            // from EmailReplyHistoryPlugin.
            replaceHistoryWithButton(view.state, view.dispatch);
          } else {
            // Record the presence of replies in the document.
            view.dispatch(view.state.tr.setMeta('repliesAlreadyInserted', true));
          }
        }, 0);
      }
      return {};
    }
  });
}
export { createInsertableReplyHistoryPlugin };